import { cn } from '@heidi/utils';

import { Chip } from '../../../chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../dropdown-menu';

import type { DataTableEnumCellProps, EnumCellOption } from './enum-cell.types';

/**
 * Picker-backed chip for representing an enum value inside a `DataTable` row.
 * The trigger renders a ghost `Chip` carrying the selected option's
 * `leadingIcon` + `label`, and click/keydown are stopped at the cell so they
 * don't propagate into the table's `onRowClick`.
 *
 * Options are rendered as a `DropdownMenuRadioGroup` so each item reports
 * `role="menuitemradio"` + `aria-checked` to assistive tech — the active
 * option is announced as "checked" instead of needing a sighted-only bg cue.
 *
 * Generic over the row's enum — pass `ClinicalTaskStatus`, `SessionStatus`,
 * `Category`, etc. as `TValue` and the callback stays type-safe. Each option
 * carries its own visual (a `StatusIndicator`, a `lucide-react` icon, an
 * `Avatar`, …) so the cell stays domain-agnostic.
 */
function DataTableEnumCell<TValue extends string>({
  value,
  options,
  selectableValues,
  onChange,
  disabled = false,
  hideLabel = false,
  clearOption,
  className,
  'data-testid': testId,
}: DataTableEnumCellProps<TValue>) {
  const selected: EnumCellOption<TValue> | undefined = options.find(
    (option) => option.value === value
  );

  // Trigger display always resolves from the full `options`; only the picker is
  // narrowed to the selectable allow-list (and away from the clear sentinel)
  // when one is supplied.
  const pickableOptions = (
    selectableValues
      ? options.filter((option) => selectableValues.includes(option.value))
      : options
  ).filter((option) => option.value !== clearOption?.value);

  const showClearItem = clearOption != null && value !== clearOption.value;
  const clearSentinelOption =
    clearOption != null
      ? options.find((option) => option.value === clearOption.value)
      : undefined;

  // Radix `RadioGroup.onValueChange` fires only when a different item is
  // picked, so the same-value no-op is implicit. The cast widens Radix's
  // generic `string` payload back to the caller's enum.
  const handleValueChange = (next: string) => {
    onChange(next as TValue);
  };

  return (
    <div
      // Interactive cell — opt out of the row's onRowClick.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      // Icon-only drops the gutter: it is label chrome, and a column sized to
      // the glyph clips the trigger without it (the cell is `overflow-hidden`).
      className={cn('w-fit', hideLabel ? 'px-0' : 'px-1')}
      role="presentation"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Chip
            color={selected?.color}
            variant="ghost"
            disabled={disabled}
            data-testid={testId}
            leadingIcon={selected?.leadingIcon}
            // Icon-only trigger is exactly its `leadingIcon` wide. The start
            // padding comes from the chip's `has-[[data-slot=chip-leading-icon]]`
            // variant, which tailwind-merge cannot drop, so only `!` outranks it.
            // `size-4` also squares off the chip's `h-6`: the body keeps
            // `rounded-full`, so the hover / active / open fill lands as a 16px
            // circle over the glyph instead of a 16×24 vertical capsule. The
            // tint therefore reads quietly under an already-tinted indicator —
            // the round shape is the contract, not the colour shift.
            className={cn(hideLabel && 'size-4 justify-center !px-0', className)}
            aria-label={hideLabel ? (selected?.label ?? value) : undefined}
          >
            {hideLabel ? null : (selected?.label ?? value)}
          </Chip>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup value={value} onValueChange={handleValueChange}>
            {pickableOptions.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                className="gap-2"
              >
                {option.leadingIcon}
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {showClearItem && clearOption && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onChange(clearOption.value)}
                data-testid={testId ? `${testId}-option-clear` : undefined}
                className="gap-2"
              >
                {clearSentinelOption?.leadingIcon}
                {clearOption.label}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

DataTableEnumCell.displayName = 'DataTableEnumCell';

export { DataTableEnumCell };
